import type { CSSProperties } from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Chip,
  Grad,
  StepRail,
  WindowFrame,
  tint,
  type ExplainerDeck,
} from "@/components/suite/SuiteExplainer";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

const ACCENT = "#4E9CFF";

/* ─────────────────────────  Sync teaching visuals  ───────────────────────── */

/** Small tool avatar — brand hue used only here, like Mail's sender avatars. */
const ToolAvatar = ({ initials, hue }: { initials: string; hue: string }) => (
  <span
    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-xs font-bold text-white"
    style={{ background: `linear-gradient(135deg, ${hue}, ${tint(hue, 0.7)})` }}
  >
    {initials}
  </span>
);

const ToolRow = ({
  initials,
  hue,
  name,
  detail,
  delay,
}: {
  initials: string;
  hue: string;
  name: string;
  detail: string;
  delay: number;
}) => (
  <div
    className="flex items-center gap-3 rounded-xl bg-white/70 px-3 py-2.5 animate-in fade-in slide-in-from-bottom-2"
    style={{ animationDelay: `${delay}ms`, animationFillMode: "both" } as CSSProperties}
  >
    <ToolAvatar initials={initials} hue={hue} />
    <div className="min-w-0 flex-1">
      <p className="truncate text-[13px] font-semibold text-[#222B45]">{name}</p>
      <p className="truncate text-xs text-genie-textSecondary">{detail}</p>
    </div>
    <Chip accent={ACCENT} className="shrink-0">
      <Check className="h-3 w-3" style={{ color: ACCENT }} />
      Connected
    </Chip>
  </div>
);

const ConnectedToolsMock = () => (
  <WindowFrame title="Connected tools" accent={ACCENT} className="max-w-md">
    {/* Genie at the top */}
    <div className="mb-3 flex items-center gap-3 rounded-xl border border-[#EEF1F6] bg-[#F9FAFC] px-3 py-2.5">
      <span
        className="flex h-11 w-11 items-center justify-center rounded-xl"
        style={{ backgroundColor: tint(ACCENT, 0.14) }}
      >
        <img src="/genie-mascots/role/operations.png" alt="" className="h-8 w-8 object-contain" />
      </span>
      <div className="min-w-0">
        <p className="text-sm font-bold text-[#222B45]">Your Genie</p>
        <p className="text-xs text-genie-textSecondary">Read, write, and act across your stack</p>
      </div>
      <Chip accent={ACCENT} tone="solid" className="ml-auto shrink-0">
        Live
      </Chip>
    </div>

    <div className="space-y-1.5">
      <ToolRow initials="HS" hue="#4E9CFF" name="HubSpot" detail="Contacts, deals, and notes" delay={0} />
      <ToolRow initials="SL" hue="#FF4DB8" name="Slack" detail="Post to any channel" delay={90} />
      <ToolRow initials="GC" hue="#FFBB33" name="Google Calendar" detail="Book and move events" delay={180} />
      <ToolRow initials="GS" hue="#4E9CFF" name="Google Sheets" detail="Append and update rows" delay={270} />
      <ToolRow initials="CR" hue="#FF4DB8" name="Your CRM" detail="Every record, kept in step" delay={360} />
    </div>
  </WindowFrame>
);

const ActionRow = ({
  label,
  tool,
  delay,
}: {
  label: string;
  tool: string;
  delay: number;
}) => (
  <div
    className="flex items-center gap-3 rounded-xl bg-white/70 px-3 py-2.5 animate-in fade-in slide-in-from-bottom-2"
    style={{ animationDelay: `${delay}ms`, animationFillMode: "both" } as CSSProperties}
  >
    <span
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
      style={{ backgroundColor: tint(ACCENT, 0.14) }}
    >
      <Check className="h-4 w-4" style={{ color: ACCENT }} />
    </span>
    <div className="min-w-0 flex-1">
      <p className="truncate text-[13px] font-semibold text-[#222B45]">{label}</p>
      <p className="truncate text-xs text-genie-textSecondary">{tool}</p>
    </div>
  </div>
);

const CallActionMock = () => (
  <WindowFrame title="After the call" accent={ACCENT} className="max-w-md">
    {/* Call ended banner */}
    <div
      className="mb-3 flex items-center gap-3 rounded-xl px-3 py-2.5"
      style={{ background: tint(ACCENT, 0.08) }}
    >
      <span
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
        style={{ backgroundColor: tint(ACCENT, 0.14) }}
      >
        <GenieIcon name="phone" size={20} className="h-5 w-5" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-bold text-[#222B45]">Call with Rachel ended</p>
        <p className="truncate text-xs text-genie-textSecondary">The Genie is running the recipe</p>
      </div>
      <Chip accent={ACCENT} className="shrink-0">
        4:12
      </Chip>
    </div>

    <div className="space-y-1.5">
      <ActionRow label="Updated HubSpot" tool="Logged the call and moved the deal" delay={120} />
      <ActionRow label="Posted to Slack" tool="Sent the summary to #sales" delay={280} />
      <ActionRow label="Booked the follow-up" tool="Held Thursday at 10:00 on the calendar" delay={440} />
    </div>

    <div className="mt-3 flex items-center gap-2 rounded-xl border border-dashed border-[#E5EAF0] px-3 py-2.5">
      <Check className="h-4 w-4" style={{ color: ACCENT }} />
      <p className="text-xs text-genie-textSecondary">
        <span className="font-semibold text-[#222B45]">No copy-paste.</span> Done before you hang up.
      </p>
    </div>
  </WindowFrame>
);

const RecipeDoneMock = () => (
  <WindowFrame title="Recipe · After every call" accent={ACCENT} className="max-w-md">
    <div className="mb-3 flex items-center justify-between">
      <span className="text-sm font-bold text-[#222B45]">Running</span>
      <Chip accent={ACCENT} tone="solid">
        <Check className="h-3 w-3" />
        Done
      </Chip>
    </div>

    <div className="space-y-2">
      {["Read the call", "Update the records", "Notify the team", "Book the next step"].map((s, i) => (
        <div
          key={s}
          className="flex items-center gap-3 animate-in fade-in slide-in-from-bottom-2"
          style={{ animationDelay: `${i * 110}ms`, animationFillMode: "both" } as CSSProperties}
        >
          <span
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-white"
            style={{ background: `linear-gradient(135deg, #4E9CFF, #FF4DB8)` }}
          >
            <Check className="h-3.5 w-3.5" />
          </span>
          <span className="text-sm font-medium text-[#222B45]">{s}</span>
        </div>
      ))}
    </div>

    <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full" style={{ backgroundColor: tint(ACCENT, 0.14) }}>
      <span
        className={cn("block h-full w-full rounded-full")}
        style={{ background: `linear-gradient(90deg, #4E9CFF, #FF4DB8)` }}
      />
    </div>
    <p className="mt-2 text-xs text-genie-textSecondary">
      Every call, the same reliable finish. The engine keeps going until it is done.
    </p>
  </WindowFrame>
);

/* ────────────────────────────────  Deck  ──────────────────────────────────── */

export const syncDeck: ExplainerDeck = {
  appId: "sync",
  enterLabel: "Enter Help Genie Sync",
  slides: [
    {
      key: "hero",
      eyebrow: "What it is",
      title: (
        <>
          One Genie, wired into <Grad>every tool you run.</Grad>
        </>
      ),
      body: "Help Genie Sync plugs your Genie into the tools you already use, so it can read, write, and act across your whole stack. No copy-paste.",
      visual: <ConnectedToolsMock />,
    },
    {
      key: "how",
      eyebrow: "How it works",
      title: (
        <>
          Connect once, then <Grad>pick a recipe.</Grad>
        </>
      ),
      body: "Give your Genie the keys one time. From then on it runs a recipe on demand and keeps going, every time a call ends, until the job is finished.",
      features: [
        { genieIcon: "lock", label: "A vault of credentials", sub: "Connect the tools you already use, kept safe and reusable." },
        { genieIcon: "book-open", label: "A library of recipes", sub: "Reusable workflows your Genie can run on demand." },
        { genieIcon: "settings", label: "A durable workflow engine", sub: "It keeps going until the job is done, not just once." },
      ],
      visual: (
        <WindowFrame title="Behind the scenes" accent={ACCENT} className="max-w-md">
          <StepRail
            accent={ACCENT}
            steps={[
              { genieIcon: "lock", title: "Connect once", sub: "Add your tools to the vault. The Genie holds the keys." },
              { genieIcon: "book-open", title: "Pick a recipe", sub: "Choose the workflow to run when a call wraps up." },
              { genieIcon: "play", title: "The Genie runs it", sub: "It reads, writes, and acts across every connected tool." },
            ]}
          />
        </WindowFrame>
      ),
    },
    {
      key: "payoff",
      eyebrow: "The payoff",
      title: (
        <>
          Every call turns into <Grad>action, not notes.</Grad>
        </>
      ),
      body: "The moment a call ends, the Genie updates your records, sends the message, and books the follow-up. You stay on the phone while the busywork finishes itself.",
      visual: <CallActionMock />,
    },
    {
      key: "enter",
      eyebrow: "Ready when you are",
      title: (
        <>
          A Genie that <Grad>gets the job done.</Grad>
        </>
      ),
      body: "Connect your first tool and the same reliable finish runs after every call. We are signing you in now.",
      visual: <RecipeDoneMock />,
    },
  ],
};
